import React, { useEffect, useState, useMemo } from 'react';
import { Alert } from 'react-bootstrap';
import FortnightRangeControl from '../Transfers/FortnightRangeControl';
import { formatCurrency } from '../../utils/formatters';
import { sumActiveFortnightlyForFortnight } from '../../utils/requirementCalculations';
import { getRequirementsTotalForBankAccount } from '../../services/BankAccountService';
import '../../css/Requirements.css';

const MISMATCH_THRESHOLD = 0.02;

const emptyReconcile = () => ({ total: null, fortnightIndex: null, error: false });

/**
 * Fortnight navigator; stored-total reconciliation runs in background and surfaces only on mismatch or error.
 */
const RequirementsFortnightBar = ({
  fortnightIndex,
  fortnightDatesIndex,
  setFortnightIndex,
  formattedRangeFortnight,
  fortnightStart,
  fortnightEnd,
  requirements,
}) => {
  const [reconcile, setReconcile] = useState(emptyReconcile);

  const bankIds = useMemo(
    () => [...new Set(requirements.map((r) => r.bank_account).filter((id) => id != null))],
    [requirements]
  );

  const fortnightSynced =
    fortnightDatesIndex != null &&
    fortnightDatesIndex === fortnightIndex &&
    fortnightStart &&
    fortnightEnd;

  const clientGrandTotal = useMemo(() => {
    if (!fortnightSynced) return 0;
    return sumActiveFortnightlyForFortnight(requirements, fortnightStart, fortnightEnd);
  }, [requirements, fortnightStart, fortnightEnd, fortnightSynced]);

  useEffect(() => {
    if (!fortnightSynced || bankIds.length === 0) {
      setReconcile(emptyReconcile());
      return undefined;
    }

    let cancelled = false;
    const fetchForIndex = fortnightIndex;
    setReconcile(emptyReconcile());

    Promise.all(
      bankIds.map(async (id) => {
        const data = await getRequirementsTotalForBankAccount(id, fetchForIndex);
        return parseFloat(data.requirements_total ?? data.calculated_amount ?? 0);
      })
    )
      .then((values) => {
        if (cancelled) return;
        setReconcile({
          total: values.reduce((a, b) => a + b, 0),
          fortnightIndex: fetchForIndex,
          error: false,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setReconcile({ total: null, fortnightIndex: fetchForIndex, error: true });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [bankIds, fortnightIndex, fortnightStart, fortnightEnd, fortnightSynced]);

  const reconcileReady =
    fortnightSynced &&
    reconcile.fortnightIndex === fortnightIndex &&
    (reconcile.total != null || reconcile.error);

  const apiGrandTotal =
    reconcile.fortnightIndex === fortnightIndex ? reconcile.total : null;
  const apiError = reconcile.fortnightIndex === fortnightIndex && reconcile.error;

  const delta =
    apiGrandTotal != null ? clientGrandTotal - apiGrandTotal : null;
  const grandMismatch =
    delta != null && Math.abs(delta) > MISMATCH_THRESHOLD;
  const showReconcileIssue =
    bankIds.length > 0 && reconcileReady && (apiError || grandMismatch);

  return (
    <div className="requirements-fortnight-bar mb-3">
      <div className="requirements-fortnight-nav d-flex justify-content-center">
        <FortnightRangeControl
          formattedRangeFortnight={formattedRangeFortnight || '…'}
          fortnightIndex={fortnightIndex}
          setFortnightIndex={setFortnightIndex}
          className="transfers-desktop-fortnight-nav d-flex align-items-center justify-content-center gap-2"
          rangeClassName="transfers-desktop-fortnight-range"
        />
      </div>

      {showReconcileIssue && (
        <div className="requirements-fortnight-reconcile mt-3">
          {apiError ? (
            <Alert variant="warning" className="mb-0 py-2 requirements-reconcile-alert">
              <strong>Could not verify totals</strong> against stored bank-account amounts for this
              fortnight. Transfers figures may still be correct.
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-0 py-2 requirements-reconcile-alert">
              <strong>Requirements total does not match stored amounts.</strong>
              <div className="mt-2 small">
                <div>
                  This page: <strong>{formatCurrency(clientGrandTotal)}</strong> / fn
                </div>
                <div>
                  Stored (bank accounts): <strong>{formatCurrency(apiGrandTotal)}</strong> / fn
                </div>
                <div className="mt-1">
                  Difference {formatCurrency(Math.abs(delta))}
                  {delta > 0 ? ' (page is higher)' : ' (stored is higher)'}.
                  Check effective dates or save a requirement to recalculate.
                </div>
              </div>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default RequirementsFortnightBar;
